/**
 * publisherGroupsService — typed client for the SuperAdmin publisher-groups
 * + audit + stats + account-managers + my-publisher-groups endpoints
 * (marketplace-publisher-groups plan).
 */
import apiClient from './apiClient';
import { ApiResponse } from '../types';
import {
  AccountManagerListing,
  AddMemberRequest,
  AuditEvent,
  AuditPage,
  CreateGroupRequest,
  GroupRole,
  MyPublisherGroup,
  PublisherGroup,
  PublisherGroupMember,
  PublisherGroupStats,
  PublisherGroupTreeNode,
  UpdateGroupRequest,
} from '../types/publisherGroups';

const API_TYPE = 'api' as const;

function unwrap<T>(response: ApiResponse<T>, what: string): T {
  if (!response.success || response.data === undefined) {
    throw new Error(response.message || `Failed to ${what}`);
  }
  return response.data;
}

class PublisherGroupsService {
  // ─────────── Group CRUD ───────────

  async list(opts?: { tree?: boolean }): Promise<PublisherGroup[] | PublisherGroupTreeNode[]> {
    const qs = opts?.tree ? '?tree=true' : '';
    const response = await apiClient.get<ApiResponse<PublisherGroup[] | PublisherGroupTreeNode[]>>(
      `/admin/super/publisher-groups${qs}`,
      undefined,
      API_TYPE,
    );
    return unwrap(response, 'list groups');
  }

  async create(body: CreateGroupRequest): Promise<PublisherGroup> {
    const response = await apiClient.post<ApiResponse<PublisherGroup>>(
      '/admin/super/publisher-groups',
      body,
      undefined,
      API_TYPE,
    );
    return unwrap(response, 'create group');
  }

  async get(groupId: string): Promise<PublisherGroup> {
    const response = await apiClient.get<ApiResponse<PublisherGroup>>(
      `/admin/super/publisher-groups/${groupId}`,
      undefined,
      API_TYPE,
    );
    return unwrap(response, 'get group');
  }

  async update(groupId: string, body: UpdateGroupRequest): Promise<PublisherGroup> {
    const response = await apiClient.put<ApiResponse<PublisherGroup>>(
      `/admin/super/publisher-groups/${groupId}`,
      body,
      undefined,
      API_TYPE,
    );
    return unwrap(response, 'update group');
  }

  async archive(groupId: string): Promise<PublisherGroup> {
    const response = await apiClient.post<ApiResponse<PublisherGroup>>(
      `/admin/super/publisher-groups/${groupId}/archive`,
      {},
      undefined,
      API_TYPE,
    );
    return unwrap(response, 'archive group');
  }

  async delete(groupId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<{ group_id: string; deleted: boolean }>>(
      `/admin/super/publisher-groups/${groupId}`,
      undefined,
      API_TYPE,
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete group');
    }
  }

  // ─────────── Membership ───────────

  async listMembers(groupId: string, role?: GroupRole | 'all'): Promise<PublisherGroupMember[]> {
    const qs = role && role !== 'all' ? `?role=${role}` : '';
    const response = await apiClient.get<ApiResponse<PublisherGroupMember[]>>(
      `/admin/super/publisher-groups/${groupId}/members${qs}`,
      undefined,
      API_TYPE,
    );
    return unwrap(response, 'list members');
  }

  async addMember(
    groupId: string,
    body: AddMemberRequest,
  ): Promise<{ member: PublisherGroupMember; alreadyMember: boolean }> {
    const response = await apiClient.post<ApiResponse<PublisherGroupMember> & { already_member?: boolean }>(
      `/admin/super/publisher-groups/${groupId}/members`,
      body,
      undefined,
      API_TYPE,
    );
    const member = unwrap(response, 'add member');
    return { member, alreadyMember: response.already_member ?? false };
  }

  async removeMember(groupId: string, userId: string, role: GroupRole): Promise<void> {
    const response = await apiClient.delete<ApiResponse>(
      `/admin/super/publisher-groups/${groupId}/members/${userId}?role=${role}`,
      undefined,
      API_TYPE,
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to remove member');
    }
  }

  // ─────────── Stats + audit ───────────

  async stats(groupId: string): Promise<PublisherGroupStats> {
    const response = await apiClient.get<ApiResponse<PublisherGroupStats>>(
      `/admin/super/publisher-groups/${groupId}/stats`,
      undefined,
      API_TYPE,
    );
    return unwrap(response, 'fetch stats');
  }

  async audit(
    groupId: string,
    opts?: { cursor?: string; limit?: number; eventType?: string; from?: string; to?: string },
  ): Promise<AuditPage> {
    const qs: string[] = [];
    if (opts?.cursor) qs.push(`cursor=${encodeURIComponent(opts.cursor)}`);
    if (opts?.limit) qs.push(`limit=${opts.limit}`);
    if (opts?.eventType) qs.push(`event_type=${opts.eventType}`);
    if (opts?.from) qs.push(`from=${encodeURIComponent(opts.from)}`);
    if (opts?.to) qs.push(`to=${encodeURIComponent(opts.to)}`);
    const tail = qs.length ? `?${qs.join('&')}` : '';
    const response = await apiClient.get<ApiResponse<AuditEvent[]>>(
      `/admin/super/publisher-groups/${groupId}/audit${tail}`,
      undefined,
      API_TYPE,
    );
    return {
      data: response.data || [],
      next_cursor: (response as any).next_cursor ?? null,
    };
  }

  async globalAudit(opts?: {
    cursor?: string;
    limit?: number;
    eventType?: string;
    actor?: string;
  }): Promise<AuditPage> {
    const qs: string[] = [];
    if (opts?.cursor) qs.push(`cursor=${encodeURIComponent(opts.cursor)}`);
    if (opts?.limit) qs.push(`limit=${opts.limit}`);
    if (opts?.eventType) qs.push(`event_type=${opts.eventType}`);
    if (opts?.actor) qs.push(`actor=${encodeURIComponent(opts.actor)}`);
    const tail = qs.length ? `?${qs.join('&')}` : '';
    const response = await apiClient.get<ApiResponse<AuditEvent[]>>(
      `/admin/super/audit${tail}`,
      undefined,
      API_TYPE,
    );
    return {
      data: response.data || [],
      next_cursor: (response as any).next_cursor ?? null,
    };
  }

  // ─────────── Helpers used outside the SuperAdmin pages ───────────

  async listAccountManagers(): Promise<AccountManagerListing[]> {
    const response = await apiClient.get<ApiResponse<AccountManagerListing[]>>(
      '/admin/super/account-managers',
      undefined,
      API_TYPE,
    );
    return unwrap(response, 'list account managers');
  }

  /** Groups the caller is a PUBLISHER of — used by the publish dialog. */
  async listMyPublisherGroups(): Promise<MyPublisherGroup[]> {
    const response = await apiClient.get<ApiResponse<MyPublisherGroup[]>>(
      '/admin/marketplace/my-publisher-groups',
      undefined,
      API_TYPE,
    );
    return unwrap(response, 'list my publisher groups');
  }
}

const publisherGroupsService = new PublisherGroupsService();
export default publisherGroupsService;
